import React, { useState, useEffect, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { Button, TextLink } from "@vds/buttons";
import { Input } from "@vds/inputs";
import { Notification } from '@vds/core';
import { Text } from '@vds/core';
import styled from "styled-components";
import {
  validateZipcode,
  setZipcode,
} from "pages/Mobility/ducks/MobilitySlice";
import { SpinnerComponent } from "common/components";

import ProtectionPlanTile from "./ProtectionPlanTile";
import { MarginSpacerL, MarginSpacerXL } from "../styleTag";

const isNumeric = /^\d+$/;

const ProtectionPlans = ({
  // featureList,
  selectedFeature,
  isLoading,
  isError,
  onZipCodeSubmit,
  onPlanClick,
  scrollIntoView = () => {},
  isMultiDevAlready = false,
  bgColor = "#FFFFFF",
  isByod = false,
  currentLineId,
  isTMPMDProtectionPlanSelected,
  isTMPMDProtectionPlanSelectedFromByod,
  reinitiateFlow = ()=>{},
  fromBYODMonarch,
  sno,
  ...props
}) => {
  const { t } = useTranslation(["app", "pdp"], { useSuspense: true });
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({
    query: "(max-width: 525px)",
  });
  const [zipCode, setZipCode] = useState();
  // const [isLoading, setLoading] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [zipError, setZipError] = useState(false);
  const [showMDNotif, setShowMDNotif] = useState(false);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [isFeatureUnavailable, setIsFeatureUnavailable] = useState(false);

  const mobilityPageData = useSelector((state) => state.MobilityPage);
  const featureList = useSelector(
    (state) => state.MobilityPage?.PDPFeatureList
  );
  const linesOnTransaction = useSelector(
    (state) => state.MobilityPage?.PDPFeatureLines
  );
  const isAPIError = !!mobilityPageData?.PDPFeatureList?.error;

  const aemContent = t("device-pdp.pdpProtectionContent", {
    ns: "pdp",
    returnObjects: true,
  });
  const featureOrder = t("device-pdp.featureOrder", {
    ns: "pdp",
    returnObjects: true,
  });
  const declineProtectionId = aemContent?.declineProtectionId || "66332";
  const multiDeviceIds = aemContent?.tmpMDCodes;

  const currentLineData = linesOnTransaction?.find(
    (i) => i?.linePackageId === currentLineId
  );
  const compatibleFeatures = currentLineData?.compatibleFeatures ?? [];
  const planList = featureList?.length
    ? featureList
        .filter(
          (i) =>
            i.billingCode !== declineProtectionId &&
            i?.spoCategoryCode !== "SS" &&
            (compatibleFeatures?.includes(i?.billingCode) || isTMPMDProtectionPlanSelected || isTMPMDProtectionPlanSelectedFromByod)
        )
        .sort((a, b) => {
          if (featureOrder?.[a?.name] > featureOrder?.[b?.name]) return 1;
          else return -1;
        })
    : [];
  const recommendedFeature = planList?.find((plan) =>
    aemContent?.featuresRecommended?.includes(plan?.billingCode)
  );
  const collapsedVisibleFeature = planList?.length
    ? selectedFeature?.featureCode &&
      selectedFeature?.featureCode !== declineProtectionId
      ? planList.find((i) => i.billingCode === selectedFeature.featureCode)
      : planList[0]
    : {};

  useEffect(() => {
    setIsFeatureUnavailable(false);
    setShowMDNotif(false);
    if (featureList?.length) {
      const isTMPMDPresent = featureList.find((feat) =>
        multiDeviceIds?.includes(feat?.billingCode)
      );
      if (
        isTMPMDPresent &&
        !isMultiDevAlready &&
        !selectedFeature?.displayName?.includes(
          "Total Mobile Protection Multi-Device"
        )
      ) {
        // show TMP-MD info banner if quantity becomes eligible
        setShowMDNotif(true);
        setShowAllPlans(true);
        scrollIntoView();
      } else {
        setShowAllPlans(false);
      }
      if (
        !!selectedFeature &&
        !featureList?.find(
          (feat) => feat?.billingCode === selectedFeature?.featureCode
        )
      ) {
        // if selected plan is no longer available, notify user
        setIsFeatureUnavailable(true);
        onPlanClick(null);
        scrollIntoView();
      }
    }
  }, [featureList, selectedFeature]);

  const onPlanSelect = (plan) => {
    setIsFeatureUnavailable(false);
    setShowAllPlans(false);
    onPlanClick(plan);
    // reinitiateFlow()
  };

  const isValidZipCode = (zip) => {
    const invalidZipCodes = t("zipCode.invalidZipcodes", {
      returnObjects: true,
    });
    let errorTexts = "";
    // dispatch(setZipcode(zip));
    if (!zip || !zip?.trim())
      errorTexts = `${t("app.addressCodePopup.zipRequired", { ns: "app" })}`;
    else if (zip?.length < 5 && !(!zip || !zip?.trim())) {
      setIsConfirm(false);
      errorTexts = `${t("app.addressCodePopup.zipLength", { ns: "app" })}`;
    } else if (invalidZipCodes.includes(zip)) {
      errorTexts = `${t("app.addressCodePopup.invalidZip", { ns: "app" })}`;
    } else if (zip?.length === 5) {
      if (!invalidZipCodes.includes(zip)) {
        setIsConfirm(true);
      } else {
        setIsConfirm(false);
        if (mobilityPageData.validZipcode === false) {
          errorTexts = `${t("app.addressCodePopup.invalidZip", {
            ns: "app",
          })}`;
        } else {
          if (!invalidZipCodes.includes(zip)) {
            dispatch(
              validateZipcode({
                zipcode: zip,
                isEmailEnable: isEmailEnable,
              })
            );
          }
        }
      }
    }
    setZipError(errorTexts);
    return !errorTexts?.length;
  };

  return (
    <div style={{ position: "relative", backgroundColor: bgColor }}>
      {isLoading && <LoadingSpinner />}
      {isError && (
        <ProtectionNotif
          aemContent={aemContent?.notifications?.noSelectionError}
        />
      )}
      {isAPIError && (
        <ProtectionNotif aemContent={aemContent?.notifications?.swwError} />
      )}
      {isFeatureUnavailable && (
        <ProtectionNotif
          aemContent={aemContent?.notifications?.featUnavailable}
        />
      )}
      {showMDNotif && (
        <ProtectionNotif
          aemContent={aemContent?.notifications?.multiDevAvail}
        />
      )}
      {mobilityPageData?.zipCode && planList?.length ? (
        <>
          {(!!isTMPMDProtectionPlanSelected || !!isTMPMDProtectionPlanSelectedFromByod) && 
            <>
              <Text kind="body" size="large">{aemContent.planSubtitle}</Text>
              <MarginSpacerL />
            </>
          }
          <PlanListContainer>
            {showAllPlans ? (
              planList.map((plan) => (
                <ProtectionPlanTile
                  key={plan.billingCode}
                  planData={plan}
                  isRecommended={
                    recommendedFeature?.billingCode === plan?.billingCode
                  }
                  selectedFeature={selectedFeature}
                  aemContent={aemContent?.plansData?.[`${plan?.billingCode}`]}
                  onClick={onPlanSelect}
                  sno={sno}
                  fromBYODMonarch={fromBYODMonarch}
                />
              ))
            ) : (
              <ProtectionPlanTile
                planData={collapsedVisibleFeature?.billingCode ? collapsedVisibleFeature : planList[0]}
                isRecommended={
                  recommendedFeature?.billingCode ===
                  collapsedVisibleFeature?.billingCode
                }
                selectedFeature={selectedFeature}
                aemContent={
                  aemContent?.plansData?.[
                    `${collapsedVisibleFeature?.billingCode ?? planList[0]?.billingCode}`
                  ]
                }
                onClick={onPlanSelect}
                sno={sno}
                fromBYODMonarch={fromBYODMonarch}
              />
            )}
          </PlanListContainer>
          {(!!!isTMPMDProtectionPlanSelected && !!!isTMPMDProtectionPlanSelectedFromByod) &&
            <MoreOptionsControl>
              <Text kind="body" size="large">{aemContent?.moreOptions?.heading}</Text>
              <TextLink
                type="standAlone"
                size="large"
                onClick={(e) => setShowAllPlans(!showAllPlans)}
                data-track={showAllPlans ? `Device | ${sno} | Device protection | Collapse | More device protection options` : `Device | ${sno} | Device protection | Expand | More device protection options`}
              >
                {showAllPlans
                  ? aemContent?.moreOptions?.collapse
                  : aemContent?.moreOptions?.expand}
              </TextLink>
            </MoreOptionsControl>
          }
        </>
      ) : !mobilityPageData?.zipCode ? (
        <ZipContainer>
          <Input
            type="text"
            maxLength={5}
            label={aemContent?.zipCode?.label ?? "ZIP Code*"}
            helperText={aemContent?.zipCode?.helperText ?? "Enter your ZIP Code to see eligible protection options."}
            helperTextPlacement="bottom"
            width={isMobile ? "100%" : "55%"}
            value={zipCode}
            error={!!zipError}
            errorText={zipError}
            onBlur={(e) => isValidZipCode(e?.target?.value)}
            onChange={(e) => {
              const zipVal = e?.target?.value
                .trim()
                .replaceAll(/\D/g, "")
                .slice(0, 5);
              if (zipVal.length === 5 && isNumeric.test(zipVal)) {
                isValidZipCode(zipVal);
              }
              if (zipVal) setZipCode(zipVal);
              else setZipCode(" ");
            }}
            onKeyDown={(e) =>{
              if(e.key === "Enter"){
                const zipVal = e.target.value.trim();
                  if (!isNaN(zipVal) && zipVal?.length == 5 && isConfirm) {
                    onZipCodeSubmit(zipVal);
                  }
              }}
            }
          />
          <div
            style={{
              width: isMobile ? "100%" : "40%",
              paddingTop: isMobile ? 0 : "1.25rem",
            }}
          >
            <Button
              size="large"
              use="secondary"
              width="100%"
              onClick={(e) => {
                const valid = isValidZipCode(zipCode);
                if (valid) {
                  // setLoading(true);
                  onZipCodeSubmit(zipCode);
                }
              }}
              data-track={`Device | ${sno} | Device protection | Explore options`}
            >
              {aemContent?.zipCode?.buttonText ?? "Explore options"}
            </Button>
          </div>
        </ZipContainer>
      ) : (
        <></>
      )}
      {/* Decline Protection - static */}
      <MarginSpacerXL />
      {(!!!isTMPMDProtectionPlanSelected && !!!isTMPMDProtectionPlanSelectedFromByod) &&
        <ProtectionPlanTile
          planData={aemContent?.plansData?.[declineProtectionId]?.data}
          aemContent={aemContent?.plansData?.[declineProtectionId]}
          selectedFeature={selectedFeature}
          onClick={onPlanClick}
          sno={sno}
          fromBYODMonarch={fromBYODMonarch}
        />
      }
    </div>
  );
};

const LoadingSpinner = () => (
  <div>
    <SpinnerComponent fullscreen={false} active={true} />
  </div>
);

const ZipContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  @media (max-width: 525px) {
    gap: 1rem;
  }
`;

const PlanListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 20px;
`;

const MoreOptionsControl = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 1rem;
`;

const ProtectionNotif = ({ aemContent }) => {
  return (
    <>
      <Notification
        kind="error"
        title={aemContent?.title}
        showCloseButton={true}
      />
      <MarginSpacerL />
    </>
  );
};

const withSuspense = (Component) => {
  return function (props) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Component {...props} />
      </Suspense>
    );
  };
};

export default withSuspense(ProtectionPlans);
